import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HardHat, CheckCircle2, Clock, AlertTriangle, Timer, Plus, Pencil, Trash2, CalendarRange, FileText, Loader2, Ban } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
// 2026-06-20 (DUAL WORKFLOW): chevron engine canonical (mirror backend).
import { CHEVRON_UNION, CHEVRON_LABEL } from "@/lib/woWorkflow";

/**
 * ProgressLapanganTab — Fase 3 + 2026-06-20 (user request).
 * - Project MAINTENANCE (is_outlet=true): KPI + strip 8 tahap WO + tabel progress per WO (tidak berubah).
 * - Project GENERAL (is_outlet=false): TANPA "Tahapan Working Order" & "Progress per Pekerjaan (WO)".
 *   Diganti input PROGRESS BULANAN manual — judul laporan & periode cut-off diisi bebas,
 *   kumulatif otomatis, dan bar "Progress Pekerjaan Keseluruhan" mengambil % kumulatif terkini
 *   (backend menyimpan physical_progress_pct = Σ progress bulanan, cap 100).
 */
// 2026-06-20 (user request DUAL WORKFLOW): strip tahap memakai chevron engine
// canonical (EKSKLUSIF) — 9 chevron aktif + Closed; Dibatalkan kartu terpisah.
const STAGE8 = [
  ...CHEVRON_UNION.map((k) => ({ key: k, label: CHEVRON_LABEL[k] })),
  { key: "closed", label: "Closed" },
];
const STAGE_CLS = {
  closed: "bg-emerald-50 border-emerald-300 text-emerald-700",
  pengiriman_invoice: "bg-sky-50 border-sky-300 text-sky-700",
  invoice_pending: "bg-sky-50 border-sky-300 text-sky-700",
  selesai_fisik: "bg-teal-50 border-teal-300 text-teal-700",
  pengerjaan: "bg-[#0B1D3A]/5 border-[#0B1D3A]/30 text-[#0B1D3A]",
};

// Format % gaya Indonesia dgn hingga 3 desimal (cth: "12,000%" / "13,355%" / "25,355%").
const fmtPct = (v) => `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 3 })}%`;
const parsePct = (v) => {
  const n = parseFloat(String(v || "").replace(",", "."));
  return isNaN(n) ? null : n;
};

const KPI_CLS = {
  red: "text-rose-700", green: "text-emerald-700", amber: "text-amber-700", navy: "text-[#0B1D3A]", slate: "text-slate-500",
};
const KpiCard = ({ icon: Icon, label, value, tone = "navy", testid }) => (
  <div className="bg-white border border-slate-200 p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5">
      <Icon size={13} className={KPI_CLS[tone]} />
      <span className="overline text-[9px] text-slate-500 tracking-wider">{label}</span>
    </div>
    <div className={`font-display text-xl tabular-nums mt-1 ${KPI_CLS[tone]}`}>{value}</div>
  </div>
);

const ProgressBar = ({ phys, detail = null }) => (
  <div className="bg-white border border-slate-200 p-4" data-testid="pl-progress-bar">
    <div className="flex items-center justify-between gap-2 text-xs mb-1.5 flex-wrap">
      <span className="font-semibold text-[#0B1D3A]">Progress Pekerjaan Keseluruhan</span>
      <span className="tabular-nums font-semibold text-[#0B1D3A]" data-testid="pl-progress-bar-value">{fmtPct(phys)}</span>
    </div>
    <div className="h-2.5 bg-slate-100"><div className="h-full bg-[#0B1D3A] transition-all" style={{ width: `${Math.min(100, phys)}%` }} /></div>
    {detail && <div className="mt-1.5 text-[10px] text-slate-500" data-testid="pl-progress-detail">{detail}</div>}
  </div>
);

const inp = "w-full border border-slate-300 px-2 h-8 text-xs bg-white rounded-none focus:outline-none focus:border-[#0B1D3A]";

/** Tampilan project GENERAL: input progress bulanan manual + kumulatif otomatis. */
function MonthlyProgressView({ project, mon, canEdit, onChanged }) {
  const s = mon?.summary || {};
  const phys = Number(s.physical_progress_pct || 0);
  const sched = s.schedule || {};
  const rem = sched.remaining_days ?? null;
  const plOverdue = sched.overdue_days || 0;
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = { title: "", period_start: "", period_end: "", progress_pct: "" };
  const [addForm, setAddForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);

  // Urutkan per periode cut-off (end) supaya kolom kumulatif berjalan kronologis.
  const entries = useMemo(() => {
    const rows = [...(mon?.project?.monthly_progress || project?.monthly_progress || [])];
    rows.sort((a, b) => String(a.period_end || a.created_at || "").localeCompare(String(b.period_end || b.created_at || "")));
    return rows;
  }, [mon, project]);

  let run = 0;
  const rows = entries.map((e) => {
    run += Number(e.progress_pct || 0);
    return { ...e, cumulative: run };
  });
  const last = rows[rows.length - 1];

  const call = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      onChanged?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal menyimpan progress bulanan.");
    } finally { setBusy(false); }
  };

  const submitAdd = () => {
    const pct = parsePct(addForm.progress_pct);
    if (!addForm.title.trim()) { toast.error("Judul laporan wajib diisi."); return; }
    if (pct == null || pct <= 0 || pct > 100) { toast.error("Progress bulan ini harus angka > 0 dan ≤ 100 (maks 3 desimal)."); return; }
    call(async () => {
      await api.post(`/active-projects/${project.id}/monthly-progress`, {
        title: addForm.title.trim(),
        period_start: addForm.period_start || null,
        period_end: addForm.period_end || null,
        progress_pct: pct,
      });
      setAddForm(emptyForm);
    }, "Progress pekerjaan bulanan ditambahkan.");
  };

  const startEdit = (e) => {
    setEditId(e.id);
    setEditForm({
      title: e.title || "",
      period_start: (e.period_start || "").slice(0, 10),
      period_end: (e.period_end || "").slice(0, 10),
      progress_pct: String(e.progress_pct ?? "").replace(".", ","),
    });
  };

  const submitEdit = (entryId) => {
    const pct = parsePct(editForm.progress_pct);
    if (!editForm.title.trim()) { toast.error("Judul laporan wajib diisi."); return; }
    if (pct == null || pct <= 0 || pct > 100) { toast.error("Progress bulan ini harus angka > 0 dan ≤ 100."); return; }
    call(async () => {
      await api.patch(`/active-projects/${project.id}/monthly-progress/${entryId}`, {
        title: editForm.title.trim(),
        period_start: editForm.period_start || null,
        period_end: editForm.period_end || null,
        progress_pct: pct,
      });
      setEditId(null);
    }, "Progress pekerjaan bulanan diperbarui.");
  };

  const submitDelete = (e) => {
    if (!window.confirm(`Hapus laporan "${e.title}"? Progress pekerjaan kumulatif akan dihitung ulang.`)) return;
    call(async () => { await api.delete(`/active-projects/${project.id}/monthly-progress/${e.id}`); }, "Laporan progress dihapus.");
  };

  return (
    <div className="space-y-4" data-testid="progress-lapangan-general">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiCard icon={HardHat} label="Progress Pekerjaan (Kumulatif)" value={fmtPct(phys)} testid="pl-progress-fisik" />
        <KpiCard icon={FileText} label="Jumlah Laporan" value={rows.length} testid="pl-jumlah-laporan" />
        <KpiCard icon={CalendarRange} label="Periode Terakhir" value={last?.period_end ? fmtDate(last.period_end) : "—"} testid="pl-periode-terakhir" />
        <KpiCard icon={Clock} label="Sisa Waktu" value={plOverdue > 0 ? `${plOverdue} hari lewat` : rem == null ? "—" : `${rem} hari`} tone={plOverdue > 0 ? "red" : "navy"} testid="pl-sisa-waktu" />
      </div>

      <ProgressBar phys={phys} />

      <div className="bg-white border border-slate-200" data-testid="pl-monthly-table">
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-[#0B1D3A]">Progress Pekerjaan Bulanan</h3>
          <span className="text-[10px] text-slate-400">Judul laporan, periode cut-off & nilai % diisi manual · kumulatif dihitung otomatis</span>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">{["Judul Laporan", "Periode Cut-off", "Progress Pekerjaan Bulan Ini", "Kumulatif", ...(canEdit ? ["Aksi"] : [])].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={canEdit ? 5 : 4} className="px-3 py-6 text-center text-[11px] text-slate-400" data-testid="pl-monthly-empty">Belum ada laporan progress. {canEdit ? "Tambahkan lewat form di bawah." : ""}</td></tr>
            )}
            {rows.map((e) => (
              editId === e.id ? (
                <tr key={e.id} className="border-b border-slate-100 bg-amber-50/40" data-testid={`pl-monthly-edit-${e.id}`}>
                  <td className="px-3 py-1.5"><input data-testid="plm-edit-title" className={inp} value={editForm.title} onChange={(ev) => setEditForm({ ...editForm, title: ev.target.value })} /></td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1">
                      <input data-testid="plm-edit-period-start" type="date" className={inp} value={editForm.period_start} onChange={(ev) => setEditForm({ ...editForm, period_start: ev.target.value })} />
                      <span className="text-slate-400 text-[10px]">s/d</span>
                      <input data-testid="plm-edit-period-end" type="date" className={inp} value={editForm.period_end} onChange={(ev) => setEditForm({ ...editForm, period_end: ev.target.value })} />
                    </div>
                  </td>
                  <td className="px-3 py-1.5"><input data-testid="plm-edit-pct" className={`${inp} tabular-nums`} value={editForm.progress_pct} onChange={(ev) => setEditForm({ ...editForm, progress_pct: ev.target.value })} placeholder="cth: 13,355" /></td>
                  <td className="px-3 py-1.5 tabular-nums text-slate-400">{fmtPct(e.cumulative)}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <button type="button" data-testid="plm-save" disabled={busy} onClick={() => submitEdit(e.id)} className="px-2 py-1 bg-[#FF5A1F] hover:bg-[#e04a13] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">{busy ? "…" : "Simpan"}</button>
                    <button type="button" data-testid="plm-cancel" disabled={busy} onClick={() => setEditId(null)} className="ml-1 px-2 py-1 border border-slate-300 text-slate-600 hover:bg-slate-50 text-[10px] uppercase tracking-wider font-semibold">Batal</button>
                  </td>
                </tr>
              ) : (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pl-monthly-row-${e.id}`}>
                  <td className="px-3 py-2 font-medium text-[#0B1D3A]">{e.title}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600 whitespace-nowrap">{e.period_start ? fmtDate(e.period_start) : "—"} <span className="text-slate-400">s/d</span> {e.period_end ? fmtDate(e.period_end) : "—"}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-700">{fmtPct(e.progress_pct)}</td>
                  <td className="px-3 py-2 tabular-nums font-semibold text-[#0B1D3A]">{fmtPct(e.cumulative)}</td>
                  {canEdit && (
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button type="button" data-testid={`plm-edit-${e.id}`} onClick={() => startEdit(e)} className="p-1 text-slate-500 hover:text-[#0B1D3A]" title="Ubah"><Pencil size={12} /></button>
                      <button type="button" data-testid={`plm-del-${e.id}`} onClick={() => submitDelete(e)} className="p-1 text-slate-400 hover:text-rose-600" title="Hapus"><Trash2 size={12} /></button>
                    </td>
                  )}
                </tr>
              )
            ))}
            {canEdit && (
              <tr className="bg-slate-50/60" data-testid="pl-monthly-add-row">
                <td className="px-3 py-2"><input data-testid="plm-title" className={inp} value={addForm.title} onChange={(ev) => setAddForm({ ...addForm, title: ev.target.value })} placeholder="cth: Progres September" /></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <input data-testid="plm-period-start" type="date" className={inp} value={addForm.period_start} onChange={(ev) => setAddForm({ ...addForm, period_start: ev.target.value })} />
                    <span className="text-slate-400 text-[10px]">s/d</span>
                    <input data-testid="plm-period-end" type="date" className={inp} value={addForm.period_end} onChange={(ev) => setAddForm({ ...addForm, period_end: ev.target.value })} />
                  </div>
                </td>
                <td className="px-3 py-2"><input data-testid="plm-pct" className={`${inp} tabular-nums`} value={addForm.progress_pct} onChange={(ev) => setAddForm({ ...addForm, progress_pct: ev.target.value })} placeholder="cth: 12,000" /></td>
                <td className="px-3 py-2 tabular-nums text-slate-400">{fmtPct(run + (parsePct(addForm.progress_pct) || 0))}</td>
                <td className="px-3 py-2">
                  <button type="button" data-testid="plm-add-btn" disabled={busy} onClick={submitAdd}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
                    {busy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Tambah
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ProgressLapanganTab({ project, mon, canEdit = false, onChanged }) {
  const nav = useNavigate();
  // 2026-06-20 (user request): project general (is_outlet=false) → progress bulanan manual.
  if (project && !project.is_outlet) {
    return <MonthlyProgressView project={project} mon={mon} canEdit={canEdit} onChanged={onChanged} />;
  }

  const s = mon?.summary || {};
  const counts = mon?.wo_stage_counts || {};
  const wos = mon?.wo_list || [];
  const woTotal = wos.length;
  // 2026-06-20 (user request): WO Dibatalkan dikeluarkan dari denominator progress
  // & jumlah aktif; WO tahap Selesai Fisik/Invoice/Closed = "selesai", bukan
  // "Sedang Dikerjakan". Definisi seragam dengan Summary Project.
  const woCancelled = Number(s.wo_cancelled ?? wos.filter((w) => w.status === "cancelled" || w.status === "cancelled_by_owner").length);
  const woDone = Number(s.wo_done ?? wos.filter((w) => ["selesai_fisik", "invoice", "closed"].includes(w.stage) || w.status === "completed").length);
  const woEff = Math.max(0, woTotal - woCancelled);
  const workPct = woEff > 0 ? (woDone / woEff) * 100 : 0;
  const belumMulai = counts.wo_diterima || 0;
  const sched = s.schedule || {};
  const rem = sched.remaining_days ?? null;
  const plOverdue = sched.overdue_days || 0;

  return (
    <div className="space-y-4" data-testid="progress-lapangan-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2">
        {[
          { icon: HardHat, label: "Progress Pekerjaan", value: `${fmtPct(workPct)} · ${woDone}/${woEff} WO`, tone: "navy", testid: "pl-progress-fisik" },
          { icon: CheckCircle2, label: "Pekerjaan Selesai", value: woDone, tone: "green", testid: "pl-selesai" },
          { icon: Timer, label: "Sedang Dikerjakan", value: Number(s.wo_active ?? (woEff - woDone)), tone: "navy", testid: "pl-dikerjakan" },
          { icon: Clock, label: "Belum Dimulai", value: belumMulai, tone: "amber", testid: "pl-belum-mulai" },
          { icon: Ban, label: "Dibatalkan", value: woCancelled, tone: "slate", testid: "pl-dibatalkan" },
          { icon: AlertTriangle, label: "Terlambat", value: s.wo_overdue ?? 0, tone: s.wo_overdue > 0 ? "red" : "navy", testid: "pl-terlambat" },
          { icon: Clock, label: "Sisa Waktu", value: plOverdue > 0 ? `${plOverdue} hari lewat` : rem == null ? "—" : `${rem} hari`, tone: plOverdue > 0 ? "red" : "navy", testid: "pl-sisa-waktu" },
        ].map((k) => (
          <KpiCard key={k.label} icon={k.icon} label={k.label} value={k.value} tone={k.tone} testid={k.testid} />
        ))}
      </div>

      <ProgressBar phys={workPct} detail={`${woDone} dari ${woEff} WO selesai${woCancelled > 0 ? ` · ${woCancelled} dibatalkan` : ""}`} />

      {/* 8 tahap WO */}
      <div className="bg-white border border-slate-200 p-4" data-testid="pl-stage-strip">
        <h3 className="text-sm font-semibold text-[#0B1D3A] mb-3">Tahapan Working Order</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
          {STAGE8.map((st) => (
            <div key={st.key} data-testid={`pl-stage-${st.key}`}
              className="border border-slate-200 p-2.5 text-center hover:border-[#0B1D3A]/40 transition-colors">
              <div className="font-display text-2xl tabular-nums text-[#0B1D3A]">{counts[st.key] || 0}</div>
              <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5 leading-tight">{st.label}</div>
            </div>
          ))}
          {/* 2026-06-20 (user request): kartu Dibatalkan — invarian Σ tahap + dibatalkan = total WO. */}
          <div data-testid="pl-stage-cancelled_owner"
            className="border border-slate-300 bg-slate-50 p-2.5 text-center">
            <div className="font-display text-2xl tabular-nums text-slate-500">{counts.cancelled_owner ?? woCancelled}</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5 leading-tight">Dibatalkan</div>
          </div>
        </div>
      </div>

      {/* Tabel progress per pekerjaan */}
      <div className="bg-white border border-slate-200" data-testid="pl-progress-table">
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-[#0B1D3A]">Progress Pekerjaan per WO</h3>
          <span className="text-[10px] text-slate-400">Klik baris untuk membuka halaman Monitoring WO</span>
        </div>
        {wos.length === 0 ? (
          <div className="p-6 text-center text-[11px] text-slate-400" data-testid="pl-empty">Belum ada Working Order pada project ini.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
            {/* 2026-06-20 (user request, project RKI/maintenance): kolom Bobot dihapus —
                pekerjaan singkat tidak butuh bobot terhadap nilai kontrak. */}
              <tr className="text-left">{["Pekerjaan / WO", "Progress Pekerjaan", "PIC", "Update Terakhir", "Tahap Proses"].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}</tr>
            </thead>
            <tbody>
              {wos.map((w) => {
                // 2026-06-20 (user request): "Sudah Selesai" = tahap Selesai Fisik /
                // Invoice / Closed; WO dibatalkan diberi badge tersendiri.
                const isCancelled = w.status === "cancelled" || w.status === "cancelled_by_owner";
                const isDone = !isCancelled && (["selesai_fisik", "invoice", "closed"].includes(w.stage) || w.status === "completed");
                const stageMeta = STAGE8.find((x) => x.key === (w.chevron || w.stage));
                return (
                  <tr key={w.id} data-testid={`pl-row-${w.id}`} onClick={() => nav(`/projects/${w.id}/monitoring`)} title="Buka Monitoring WO"
                    className="border-b border-slate-100 hover:bg-sky-50/60 cursor-pointer transition-colors">
                    <td className="px-3 py-2"><div className="font-medium text-[#0B1D3A]">{w.wo_number || `#${w.id.slice(0, 6)}`}</div><div className="text-[10px] text-slate-500">{w.client || ""}</div></td>
                    <td className="px-3 py-2 min-w-[130px]" data-testid={`pl-work-progress-${w.id}`}>
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${isCancelled ? "bg-slate-100 border-slate-300 text-slate-500 line-through" : isDone ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-amber-50 border-amber-300 text-amber-700"}`}>
                        {isCancelled ? "Dibatalkan" : isDone ? "Sudah Selesai" : "Belum Selesai"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{w.pic || "—"}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-500">{w.updated_at ? fmtDate(w.updated_at) : "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${isCancelled ? "bg-slate-100 border-slate-300 text-slate-500" : STAGE_CLS[w.chevron] || "bg-slate-50 border-slate-300 text-slate-600"}`}>
                        {isCancelled ? "Dibatalkan" : stageMeta?.label || w.chevron_label || w.stage}
                      </span>
                      {(w.late_status || w.overdue) && !isCancelled && <span className="ml-1 inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-rose-50 border-rose-300 text-rose-700">{w.late_status || "Terlambat"}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
